import {
  buildSearchEngine,
  getOrganizationEndpoints,
  buildResultList,
  ResultList,
  ResultListOptions,
  buildSearchBox,
  SearchBox,
  SearchBoxOptions,
  buildFacet,
  FacetOptions,
  Facet,
  SearchEngine,
  buildFacetManager,
  buildSort,
  buildDateSortCriterion,
  SortByDate,
  SortOrder,
  buildFieldSortCriterion,
  SortByField,
  SortBy,
  buildRelevanceSortCriterion,
  SortByRelevancy,
  buildContext,
  buildDictionaryFieldContext,
  loadAdvancedSearchQueryActions,
} from '@coveo/headless';
import { useVSFContext } from '@vue-storefront/core';
import { ref } from '@nuxtjs/composition-api';
import { coveoFieldsToInclude } from '~/common';
import { useCoveoStore } from '~/stores/coveo-store';

const facets = ref(null);
let engine = null;
let facetManager = null;
let sort = null;
let searchBox: SearchBox = null;
let resultList: ResultList = null;
let advancedSearchQueryActions = null;

const useCoveoHeadlessSearch = () => {
  const context = useVSFContext();
  const coveoStore = useCoveoStore();


  if(!engine) {
    engine = buildSearchEngine({
      configuration: {
        organizationId: context.$config.coveoEnv,
        organizationEndpoints: getOrganizationEndpoints(
          context.$config.coveoEnv
        ),
        accessToken: coveoStore.getAuthToken,
        search: {
          searchHub: `${context.i18n.localeProperties.coveoSearchHub}_${context.i18n.localeProperties.languageCode}`,
          locale: context.i18n.localeProperties.languageCode
        },
        analytics: {
          enabled: true,
        },
        renewAccessToken: () => coveoStore.renewAccessToken(context),
      },
    });

    buildContext(engine).add("Search", "Moomin");
    buildContext(engine).add(
      "country",
      context.i18n.localeProperties.countryCode?.toUpperCase()
    );
    const dictionaryFieldContext = buildDictionaryFieldContext(engine);
    dictionaryFieldContext.set({
      lowest_price: context.i18n.localeProperties.baseStoreCode,
      price_currency: context.i18n.localeProperties.baseStoreCode,
      price_discounted: context.i18n.localeProperties.baseStoreCode,
      price_discounted_2001: context.i18n.localeProperties.baseStoreCode,
      price_discountpercentage: context.i18n.localeProperties.baseStoreCode,
      price_discountpercentage_2001: context.i18n.localeProperties.baseStoreCode,
      price_regular: context.i18n.localeProperties.baseStoreCode,
      price_regular_2001: context.i18n.localeProperties.baseStoreCode,
      price_discounted_6001: context.i18n.localeProperties.baseStoreCode,
      price_discountpercentage_6001: context.i18n.localeProperties.baseStoreCode
    });

    engine.executeFirstSearch
  }
  if (advancedSearchQueryActions == null) {
    advancedSearchQueryActions = loadAdvancedSearchQueryActions(engine).registerAdvancedSearchQueries({aq: ""});
  }

  const searchBoxOptions: SearchBoxOptions = {
    numberOfSuggestions: 5
  }

  const resultListOptions: ResultListOptions = {
    fieldsToInclude: coveoFieldsToInclude
  };

  const facetNames = [
    'classification_character',
    'classification_productspecifictype',
    // 'simplifiedcolorcode',
    'classification_collection',
    'classification_productmaterial',
    'classification_size',
    'classification_matchingset',
  ];

  const buildFacets = (facetNames: string[], engine: SearchEngine): Facet[] => {
    let facets: Facet[] = [];
    facetNames.forEach(facetName => {
      const facetOptions: FacetOptions = {
        field: facetName,
        facetId: facetName,
        numberOfValues: 5,
      };
      const facet = buildFacet(engine, {options: facetOptions});
      facets.push(facet);
    });
    return facets;
  };


  const sortByDate: SortByDate = buildDateSortCriterion(SortOrder.Ascending);

  const buildSortingObject = (sortBy: SortBy = SortBy.Relevancy, sortOrder: SortOrder = SortOrder.Ascending, field?: string): SortByDate | SortByField | SortByRelevancy => {
    switch (sortBy) {
      case SortBy.Date:
        return buildFieldSortCriterion(field, sortOrder);
      case SortBy.Field:
        return buildFieldSortCriterion(field, sortOrder);
      case SortBy.Relevancy:
        return buildRelevanceSortCriterion();
      default:
        return buildRelevanceSortCriterion();
    }
  }

  if (facets.value === null) {
    facets.value = buildFacets(facetNames, engine);
  }

  if (facetManager === null) {
    facetManager = buildFacetManager(engine);
  }

  // Always sort by relevance on first call
  if(sort === null) {
    sort = buildSort(engine, { initialState: { criterion: buildSortingObject() } });
  }
  if(searchBox === null) {
    searchBox = buildSearchBox(engine, { options: searchBoxOptions });
  }
  if(resultList === null) {
    resultList = buildResultList(engine, { options: resultListOptions });
  }

  return {
    engine,
    searchBox,
    resultList,
    facetManager,
    sort,
    facets,
    buildSortingObject
  }
};

export default useCoveoHeadlessSearch;
